import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingSpinner,
  PageContainer,
  toast,
} from '@startupquickstart/frontend';
import { ChevronRight, LifeBuoy, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useLinearAuthStatus,
  useMyTickets,
  useStartLinearAuth,
} from '@/api/support';
import { NewTicketDialog, StatusBadge } from '@/components/support';

/**
 * Top-level Support landing. Three states:
 *  - not connected → empty state with Connect Linear CTA
 *  - connected, no tickets → empty state with File first ticket CTA
 *  - connected, tickets → list with quick-glance status + labels
 */
/**
 * Imperatively start the Linear OAuth flow. We never navigate to an
 * intermediate "connect" page — clicking Connect should land the user on
 * Linear's grant screen with one click.
 */
function useStartConnect(): { start: () => void; isPending: boolean } {
  const startAuth = useStartLinearAuth();
  return {
    isPending: startAuth.isPending,
    start: async () => {
      try {
        const { authorizeUrl } = await startAuth.mutateAsync();
        window.location.assign(authorizeUrl);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Could not start Linear connect',
        );
      }
    },
  };
}

export function SupportTicketsPage() {
  const { data: authStatus, isLoading: authLoading } = useLinearAuthStatus();
  const { start, isPending } = useStartConnect();
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  if (authLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (!authStatus?.connected) {
    return (
      <PageContainer>
        <Header onConnect={start} connectPending={isPending} />
        <EmptyState
          icon={<LifeBuoy className="h-10 w-10" />}
          title="Connect Linear to get started"
          description="Tickets you file appear in our Linear Support team under your account so you can follow their progress."
          action={{
            label: isPending ? 'Redirecting…' : 'Connect Linear',
            onClick: start,
          }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header connected onNewTicket={() => setNewTicketOpen(true)} />
      <ConnectedTicketList onNewTicket={() => setNewTicketOpen(true)} />
      <NewTicketDialog open={newTicketOpen} onOpenChange={setNewTicketOpen} />
    </PageContainer>
  );
}

function Header({
  connected,
  onConnect,
  connectPending,
  onNewTicket,
}: {
  connected?: boolean;
  onConnect?: () => void;
  connectPending?: boolean;
  onNewTicket?: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Support</h1>
        <p className="text-muted-foreground">
          File bug reports, feature requests, and content fixes. Tickets are
          tracked in Linear under your account.
        </p>
      </div>
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
        {connected ? (
          <>
            <Button
              variant="outline"
              onClick={() => navigate('/internal/support/connect')}
            >
              Manage Linear
            </Button>
            <Button onClick={onNewTicket}>
              <Plus className="mr-2 h-4 w-4" />
              New ticket
            </Button>
          </>
        ) : (
          <Button onClick={onConnect} disabled={connectPending}>
            {connectPending ? 'Redirecting…' : 'Connect Linear'}
          </Button>
        )}
      </div>
    </div>
  );
}

function ConnectedTicketList({ onNewTicket }: { onNewTicket: () => void }) {
  const navigate = useNavigate();
  const { data: tickets, isLoading, isError, error } = useMyTickets();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Could not load tickets"
        description={error instanceof Error ? error.message : 'Unknown error'}
      />
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <EmptyState
        icon={<LifeBuoy className="h-10 w-10" />}
        title="No tickets yet"
        description="File the first one when something needs attention — or click New ticket above."
        action={{
          label: 'File a ticket',
          icon: Plus,
          onClick: onNewTicket,
        }}
      />
    );
  }

  return (
    <ul className="divide-y rounded-xl border bg-card shadow-sm">
      {tickets.map((t) => (
        <li key={t.id}>
          <button
            type="button"
            onClick={() => navigate(`/internal/support/${t.id}`)}
            className="group flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:bg-muted/40"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {t.identifier}
                </code>
                <h3 className="truncate text-base font-medium text-foreground">
                  {t.title}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <StatusBadge state={t.state} />
                {t.labels.slice(0, 4).map((l) => (
                  <Badge
                    key={l.id}
                    variant="outline"
                    className="font-normal text-muted-foreground"
                  >
                    {l.name}
                  </Badge>
                ))}
                {t.labels.length > 4 && (
                  <span className="text-muted-foreground">
                    +{t.labels.length - 4} more
                  </span>
                )}
                <span className="ml-auto whitespace-nowrap text-muted-foreground">
                  Updated {new Date(t.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            <ChevronRight
              className="h-5 w-5 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground"
              aria-hidden
            />
          </button>
        </li>
      ))}
    </ul>
  );
}
